class Select extends Dropdown {
  constructor() {
    super();

    this.EXPANDED_CLASS = 'select--expanded';
    this.ACTIVE_OPTION_CLASS = 'select__option--active';
    this.emitEvent = this.hasAttribute("data-emit");

    this.activeOption = this.selectedOption || this.options[0];
  }

  /**
   * Adds event listeners to the trigger and options.
   */
  addListeners() {
    super.addListeners();

    this.options.forEach(option => {
      option.addEventListener('click', event => this.selectOption(event));
    });

    subscribe(PUB_SUB_EVENTS.selectOption, value => {
      const option = this.findOptionByValue(value);

      if (option) this.selectOption({ target: option });
    });
  }

  close() {
    super.close();

    publish(PUB_SUB_EVENTS.optionSelected, {
      value: this.activeOption.dataset.selectOption,
      index: this.activeOption.dataset.selectOptionIndex,
      id: this.dataset.id
    });
  }

  /**
   * Selects an option from the dropdown.
   * @param event
   */
  selectOption(event) {
    const option = event.target.closest("[data-select-option]");

    if (this.activeOption === option) return;

    option.setAttribute('aria-selected', "true");

    this.activeOption.setAttribute('aria-selected', "false");
    this.activeOption.classList.remove(this.ACTIVE_OPTION_CLASS);
    this.activeOption = option;
    this.activeOption.classList.add(this.ACTIVE_OPTION_CLASS);

    this.trigger.setAttribute('aria-activedescendant', option.getAttribute('id'));

    if (this.selectedText) this.selectedText.innerHTML = option.dataset.selectOption;
    if (this.hiddenInput) this.hiddenInput.value = option.dataset.selectOption;
    if (this.emitEvent) this.hiddenInput.dispatchEvent(new Event("input", { bubbles: true }));

    this.close();
    this.trigger.focus();
  }

  /**
   * Selects the next option in the list.
   */
  selectNextOption() {
    if (this.activeOption) {
      const currentOptionIndex = Number(this.activeOption.dataset.selectOptionIndex);

      if (currentOptionIndex + 1 < this.options.length) {
        const nextOption = this.options[currentOptionIndex + 1];
        this.selectOption({ target: nextOption });
      }
    }
  }

  /**
   * Selects the previous option in the list.
   * If the first option is already selected, it does nothing.
   */
  selectPreviousOption() {
    if (this.activeOption) {
      const currentOptionIndex = Number(this.activeOption.dataset.selectOptionIndex);

      if (currentOptionIndex - 1 >= 0) {
        const previousOption = this.options[currentOptionIndex - 1];
        this.selectOption({ target: previousOption });
      }
    }
  }

  /**
   * Handles key presses on the select trigger.
   * @param event
   */
  handleTriggerKeyPress(event) {
    event.preventDefault();

    switch (event.key) {
      case 'ArrowUp':
        this.isExpanded ? this.selectPreviousOption() : this.open();
        break;

      case 'ArrowDown':
        this.isExpanded ? this.selectNextOption() : this.open();
        break;

      case 'Tab':
        this.close();
        break;

      case 'Escape':
      case 'Enter':
        this.isExpanded ? this.close() : this.open();
    }
  }

  findOptionByValue(value) {
    return Array.from(this.options).find(option => option.dataset.selectOption === value);
  }

  /**
   * @returns {HTMLElement} The select trigger element
   */
  get trigger() {
    return this.querySelector("[data-select-trigger]");
  }

  /**
   * @returns {Element}
   */
  get selectedOption() {
    return this.querySelector("[data-select-selected]");
  }

  /**
   * @returns {HTMLElement} The selected text element
   */
  get selectedText() {
    return this.trigger.querySelector("[data-selected-text]");
  }

  /**
   * @returns {HTMLElement} The dropdown element
   */
  get dropdown() {
    return this.querySelector("[data-select-dropdown]");
  }

  /**
   * @returns {NodeListOf<Element>} All option elements
   */
  get options() {
    return this.dropdown.querySelectorAll("[data-select-option]");
  }

  /**
   * @returns {HTMLInputElement|null} The hidden input element
   */
  get hiddenInput() {
    return this.querySelector("[data-select-input]");
  }
}

if (!customElements.get('select-wrapper')) {
  customElements.define('select-wrapper', Select);
}